import React from "react";
import { BarChart, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Campaign,
  CampaignState,
  CampaignType,
  Response,
  Team,
  Team as TeamType,
  User,
  AssignmentSubmission,
} from "../../types";
import { Link } from "react-router-dom";
import { UserList } from "../User/List";
import { ResultsButton } from "./ResultButton";
import { PreviewButton } from "./PreviewButton";
import { RespondButton } from "./RespondButton";
import { ReopenButton } from "./ReopenButton";
import { AssignmentSubmissionButton } from "./AssignmentSubmissionButton";

export interface Props {
  campaign: Campaign;
  team: TeamType;
  responses: Response[];
  assignmentSubmissions?: AssignmentSubmission[];
  users: User[];
  onExclude: (campaign: Campaign, team: Team) => void;
}

export const CampaignTeam = ({
  campaign,
  team,
  responses,
  assignmentSubmissions = [],
  users,
  onExclude,
}: Props) => {
  function getUser(response: Response) {
    return users.find((user) => user.id === response.authorId);
  }

  function handleExcludeClick(team: Team) {
    onExclude(campaign, team);
  }

  const responseAuthors = responses
    .map(getUser)
    .filter((target) => target !== undefined) as User[];

  const submissionAuthors = assignmentSubmissions
    .map((submission) => users.find((user) => user.id === submission.authorId))
    .filter((target) => target !== undefined) as User[];

  // const responseSubjectUsers = responses
  //   .flatMap((response) => response.responseSubjects.filter((rs) => rs.subjectUserId !== null).map((rs) => rs.subjectUserId))
  //   .map((id) => users.find((user) => user.id === id))
  //   .filter((target) => target !== undefined) as User[];

  const responseSubjectUsers = team.userIds
    .map((id) => users.find((user) => user.id === id))
    .filter((target) => target !== undefined) as User[];

  // Create a Set to track user IDs we've already included to avoid duplicates
  const includedUserIds = new Set<string>();
  const allUsers: User[] = [];

  // Add response authors first
  responseAuthors.forEach((user) => {
    if (!includedUserIds.has(user.id)) {
      allUsers.push(user);
      includedUserIds.add(user.id);
    }
  });

  // Add submission authors (skip if already included)
  submissionAuthors.forEach((user) => {
    if (!includedUserIds.has(user.id)) {
      allUsers.push(user);
      includedUserIds.add(user.id);
    }
  });

  // Add remaining team members who don't have responses or submissions
  responseSubjectUsers.forEach((user) => {
    if (!includedUserIds.has(user.id)) {
      allUsers.push(user);
      includedUserIds.add(user.id);
    }
  });

  const campaignDueDate = campaign.dueDate ? new Date(campaign.dueDate) : null;

  // Determine if we should show assignment submission buttons or response buttons
  const showAssignmentSubmissions =
    campaign.campaignType === CampaignType.Assignment &&
    campaign.state === CampaignState.AssignmentActive;

  // Per-member actions (reopen/respond/preview/results) only exist once the
  // campaign is live; draft and scheduled rows render none.
  const showUserActions = ![
    CampaignState.Draft,
    CampaignState.AssignmentScheduled,
    CampaignState.Scheduled,
  ].includes(campaign.state);

  // Show placeholder card if team is hidden
  if (team.isHidden) {
    return (
      <Card elevation={0} sx={{ opacity: 0.6 }}>
        <CardHeader
          title={team.name}
          subheader="Team hidden"
          sx={{
            py: 1,
          }}
        />
        <CardContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            This team is not assigned to you. You do not have access to view
            team members or results.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={0}>
      <CardHeader
        title={team.name}
        subheader={`${team.userIds.length || 0} members`}
        sx={{
          py: 1,
        }}
        action={
          campaign.state === CampaignState.Draft ? (
            <IconButton
              edge="end"
              aria-label="exclude from campaign"
              // component={Link}
              // to={`/results/${campaign.id}/PR/${team.id}/`}
              sx={{ mr: "12px" }}
              onClick={() => handleExcludeClick(team)}
            >
              <DeleteIcon />
            </IconButton>
          ) : campaign.state === CampaignState.Scheduled ? (
            <></> // No button in scheduled mode
          ) : (
            <Tooltip title="View Team Results">
              <IconButton
                edge="end"
                aria-label="results"
                component={Link}
                to={`/results/${campaign.id}/PR/${team.id}/`}
                sx={{ mr: "12px" }}
              >
                <BarChart />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <CardContent>
        <UserList
          responses={responses}
          users={allUsers}
          // FL-84: the action cluster (reopen + respond + preview + results)
          // is wider than the single-icon default reservation. FL-361: reserve
          // nothing when no actions render, so names get the full row.
          actionsReserve={showUserActions ? { xs: 14, sm: 18 } : 0}
          loopResponses={
            campaign.loopStudent ? campaign.loopFeedbackSummary : []
          }
          leader={team.leaderId}
          campaignDueDate={campaignDueDate}
          // FL-361: omit the prop entirely (rather than render an empty
          // fragment) — any non-null secondaryAction makes MUI's ListItem
          // keep 48px of right padding.
          UserActions={
            !showUserActions
              ? undefined
              : ({ user }) => (
                  <>
                    {showAssignmentSubmissions ? (
                      <AssignmentSubmissionButton
                        user={user}
                        assignmentSubmissions={assignmentSubmissions}
                        submissionAuthors={submissionAuthors}
                      />
                    ) : (
                      <>
                        <ReopenButton
                          user={user}
                          responses={responses}
                          responseAuthors={responseAuthors}
                        />
                        <RespondButton
                          user={user}
                          responses={responses}
                          responseAuthors={responseAuthors}
                        />
                      </>
                    )}
                    <PreviewButton
                      campaign={campaign}
                      user={user}
                      disabled={user.isInstructor === true}
                    />
                    <ResultsButton
                      campaign={campaign}
                      team={team}
                      user={user}
                      responses={responses}
                    />
                  </>
                )
          }
        />
      </CardContent>
    </Card>
  );
};
